import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../util/fieldHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

// How many "at a glance" specs fit on a card before it stops being scannable.
const MAX_CARD_SPECS = 4;

// Schema types that can be rendered as a short spec value.
// 'text', 'multi-enum' and 'youtube' are deliberately excluded: they are too long for a card.
const CARD_SPEC_SCHEMA_TYPES = ['enum', 'long', 'boolean'];

// A marketplace may name its condition field anything (it is authored in Console),
// so we detect it by key instead of assuming a single hardcoded key exists.
const CONDITION_KEY_PATTERN = /condition/i;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Turn a raw spec value into a short display string.
 *
 * Note: numbers are deliberately NOT locale-grouped. `getDetailCustomFieldValue` (used by
 * the ListingPage spec table) returns the raw number, so grouping here would make the same
 * field disagree between the card and the detail page — e.g. a `modelYear` of 2023 renders
 * as "2,023" on the card and "2023" in the spec table. Numbers are coerced with String()
 * so both surfaces agree.
 */
const formatSpecValue = (value, intl) => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  // Objects/arrays are never renderable as a one-line spec.
  return null;
};

/**
 * Collect the "at a glance" specs shown on the card.
 *
 * IMPORTANT: nothing here is hardcoded to a particular marketplace. Both the set of
 * fields and their labels come from `config.listing.listingFields`, which in production
 * is authored in Sharetribe Console. Fields that are not relevant for this listing's
 * listing type or category are skipped, as are fields the listing simply does not have.
 *
 * @param {Object} listing API entity: listing or ownListing
 * @param {Object} config app configuration (e.g. from useConfiguration())
 * @param {Object} intl React Intl instance
 * @returns {Object} { specs: Array<{key, label, value}>, conditionSpec: Object|null }
 */
export const getListingCardSpecs = (listing, config, intl) => {
  const publicData = listing?.attributes?.publicData || {};
  const listingFields = config?.listing?.listingFields || [];
  const listingType = publicData.listingType;

  // Resolve the listing's category path so category-limited fields can be filtered out.
  const { key: categoryPrefix, categories: categoryLevelOptions = [] } =
    config?.categoryConfiguration || {};
  const categoriesObj = categoryPrefix
    ? pickCategoryFields(publicData, categoryPrefix, 1, categoryLevelOptions)
    : {};
  const currentCategories = Object.values(categoriesObj);

  const cat1Id = categoriesObj.categoryLevel1;
  const cat2Id = categoriesObj.categoryLevel2;
  const cat3Id = categoriesObj.categoryLevel3;
  const cat1 = categoryLevelOptions.find(c => c.id === cat1Id);
  const cat2 = cat1?.subcategories?.find(c => c.id === cat2Id);
  const cat3 = cat2?.subcategories?.find(c => c.id === cat3Id);

  const categorySpecs = [];
  const isEmoto = listingType === 'emoto-sale' || cat1Id === 'emoto' || cat1Id === 'emotos';
  const isParts = listingType === 'parts-sale' || cat1Id === 'parts';

  if (isEmoto) {
    if (cat2?.name) {
      categorySpecs.push({
        key: 'brand',
        label: intl.formatMessage({ id: 'ListingCard.brandLabel', defaultMessage: 'Brand' }),
        value: cat2.name,
      });
    }
    if (cat3?.name) {
      categorySpecs.push({
        key: 'model',
        label: intl.formatMessage({ id: 'ListingCard.modelLabel', defaultMessage: 'Model' }),
        value: cat3.name,
      });
    }
  } else if (isParts) {
    if (cat2?.name) {
      categorySpecs.push({
        key: 'partCategory',
        label: intl.formatMessage({
          id: 'ListingCard.partCategoryLabel',
          defaultMessage: 'Part category',
        }),
        value: cat2.name,
      });
    }
  }

  const allSpecs = listingFields.reduce((picked, fieldConfig) => {
    const { key, schemaType, enumOptions, showConfig = {}, scope = 'public' } = fieldConfig || {};

    // Only public data is available on a search result card.
    if (!key || scope !== 'public') {
      return picked;
    }
    if (!CARD_SPEC_SCHEMA_TYPES.includes(schemaType)) {
      return picked;
    }
    // `showConfig.isDetail: false` is how Console says "do not render this enum/long/boolean
    // as a detail row". The ListingPage spec table honours it, so the card must too —
    // otherwise a field the operator deliberately hid still shows up in search results.
    // validShowConfig defaults isDetail to true, so an absent value means "show".
    if (showConfig.isDetail === false) {
      return picked;
    }
    if (!isFieldForListingType(listingType, fieldConfig)) {
      return picked;
    }
    if (!isFieldForCategory(currentCategories, fieldConfig)) {
      return picked;
    }

    const rawValue = getFieldValue(publicData, key);
    if (rawValue == null || rawValue === '') {
      return picked;
    }

    const { label } = showConfig;
    const detail = getDetailCustomFieldValue(
      enumOptions,
      rawValue,
      schemaType,
      key,
      label,
      intl,
      'ListingCard'
    );

    let displayLabel = label;
    let value = formatSpecValue(detail?.value, intl);

    // Format mileage specifically with unit and clear label
    if (key === 'odometerMiles' && typeof rawValue === 'number') {
      displayLabel = intl.formatMessage({ id: 'ListingCard.mileageLabel', defaultMessage: 'Mileage' });
      value = `${rawValue.toLocaleString()} mi`;
    }

    // Never render an empty row: both a label and a value are required.
    if (!value || !displayLabel) {
      return picked;
    }
    return picked.concat({ key, label: displayLabel, value });
  }, []);

  const combinedSpecs = [...categorySpecs, ...allSpecs];

  // The condition-style field (if the marketplace defines one) is promoted to a badge.
  const conditionSpec = combinedSpecs.find(spec => CONDITION_KEY_PATTERN.test(spec.key)) || null;
  const specs = combinedSpecs
    .filter(spec => spec !== conditionSpec)
    .slice(0, MAX_CARD_SPECS);

  return { specs, conditionSpec };
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 *   - isBusiness: boolean indicating if seller is registered business / merchant
 */
/**
 * Format raw address string into a friendly City, State format.
 */
export const formatLocationDisplay = rawAddress => {
  if (!rawAddress || typeof rawAddress !== 'string') return null;
  const parts = rawAddress.split(',').map(p => p.trim());
  if (parts.length >= 3) {
    const statePart = parts[parts.length - 2]?.replace(/\s*\d{5}(-\d{4})?/, '').trim();
    const cityPart = parts[parts.length - 3] || parts[0];
    return `${cityPart}, ${statePart}`;
  } else if (parts.length === 2) {
    return `${parts[0]}, ${parts[1].replace(/\s*\d{5}(-\d{4})?/, '').trim()}`;
  }
  return rawAddress;
};

export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};
  const rawLocation = publicData?.location?.address || publicData?.location?.search;
  const locationDisplay = formatLocationDisplay(rawLocation);

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorPublicData = listing?.author?.attributes?.profile?.publicData;
  const isBusiness = authorPublicData?.userType === 'business';
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: isBusiness && authorPublicData?.businessName ? authorPublicData.businessName : authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Single formatted price line (amount + per-unit if applicable); used for price block
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  // Plain string price for screen readers to avoid stringifying React element objects into "[object Object]"
  const plainPriceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage(
          { id: priceMessageId },
          { priceValue: formattedPrice, pricePerUnit: perUnitString }
        )
      : '';

  const cardAriaLabel =
    plainPriceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: plainPriceMessage }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    isBusiness,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    locationDisplay,
  };
};
